import OpenAI from 'openai';
import BaseAIProvider from '../core/BaseAIProvider';
import { CV_EXTRACTION_PROMPT, IMMIGRATION_ANALYSIS_PROMPT } from '../config/promptTemplates';

/**
 * OpenAI Provider
 * Supports GPT-4, GPT-4o, GPT-3.5, and compatible APIs (DeepSeek, Azure, etc.)
 */
class OpenAIProvider extends BaseAIProvider {
  constructor(config = {}) {
    super(config);

    this.client = new OpenAI({
      apiKey: this.apiKey,
      baseURL: this.baseUrl || 'https://api.openai.com/v1',
      dangerouslyAllowBrowser: true // Required for client-side usage
    });
  }

  getProviderName() {
    return 'openai';
  }

  supportsStreaming() {
    return true;
  }

  getPricing() {
    // Default pricing for gpt-4o-mini
    if (this.model.includes('gpt-4o-mini')) {
      return {
        inputCostPerMillion: 0.15,
        outputCostPerMillion: 0.60,
        currency: 'USD'
      };
    } else if (this.model.includes('gpt-4o')) {
      return {
        inputCostPerMillion: 2.50,
        outputCostPerMillion: 10.00,
        currency: 'USD'
      };
    } else if (this.model.includes('gpt-4-turbo')) {
      return {
        inputCostPerMillion: 10.00,
        outputCostPerMillion: 30.00,
        currency: 'USD'
      };
    } else if (this.model.includes('gpt-3.5')) {
      return {
        inputCostPerMillion: 0.50,
        outputCostPerMillion: 1.50,
        currency: 'USD'
      };
    }

    return super.getPricing();
  }

  getRateLimits() {
    return {
      requestsPerMinute: 500,
      tokensPerMinute: 200000,
      requestsPerDay: 10000
    };
  }

  /**
   * Parse JSON from OpenAI response
   */
  parseJSON(text) {
    if (!text || typeof text !== 'string') return null;

    try {
      // Remove code fences if present
      let cleaned = text.trim();
      cleaned = cleaned.replace(/```json\s*/g, '').replace(/```\s*/g, '');

      // Try to parse directly
      return JSON.parse(cleaned);
    } catch (error) {
      // Try to find JSON object in text
      const startIdx = text.indexOf('{');
      const endIdx = text.lastIndexOf('}');

      if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
        try {
          return JSON.parse(text.slice(startIdx, endIdx + 1));
        } catch (_) {
          console.error('[OpenAI Provider] Failed to parse JSON:', error);
          return null;
        }
      }

      return null;
    }
  }

  /**
   * Analyze CV - Implementation
   */
  async analyzeCV(cvText, options = {}) {
    try {
      console.log('[OpenAI Provider] Starting CV extraction...');
      this.validateConfig();

      const prompt = CV_EXTRACTION_PROMPT(cvText);

      const completion = await this.client.chat.completions.create({
        model: this.model,
        messages: [
          {
            role: 'system',
            content: 'You are an expert resume parser for immigration assessments. Extract structured data accurately and return only valid JSON.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: options.temperature ?? 0.1,
        max_tokens: options.maxTokens || 2048,
        top_p: options.topP ?? 0.95,
        response_format: { type: 'json_object' } // Force JSON mode
      });

      const responseText = completion.choices[0]?.message?.content;

      if (!responseText) {
        throw new Error('No response from OpenAI');
      }

      const parsed = this.parseJSON(responseText);

      if (!parsed) {
        console.error('[OpenAI Provider] Failed to parse CV response');
        return this.buildFallbackCVData(cvText, 'Failed to parse response');
      }

      return {
        personalInfo: parsed.personalInfo || {},
        education: Array.isArray(parsed.education) ? parsed.education : [],
        experience: Array.isArray(parsed.experience) ? parsed.experience : [],
        skills: parsed.skills || { technical: [], programming: [], tools: [], certifications: [] },
        summary: parsed.summary || '',
        strengths: Array.isArray(parsed.strengths) ? parsed.strengths : [],
        languageProficiency: parsed.languageProficiency || { english: 'unknown', other: [] },
        assessment: parsed.assessment || { score: 5, reasoning: 'Unable to assess' },
        totalExperienceYears: parsed.totalExperienceYears || '',
        rawText: parsed.rawText || cvText,
        provider: this.getProviderName(),
        model: this.getModelName(),
        usage: {
          promptTokens: completion.usage?.prompt_tokens || 0,
          completionTokens: completion.usage?.completion_tokens || 0,
          totalTokens: completion.usage?.total_tokens || 0
        }
      };
    } catch (error) {
      console.error('[OpenAI Provider] CV analysis error:', error);
      return this.buildFallbackCVData(cvText, error.message);
    }
  }

  /**
   * Build fallback CV data
   */
  buildFallbackCVData(rawText = '', errorMessage = '') {
    return {
      personalInfo: {},
      education: [],
      experience: [],
      skills: { technical: [], programming: [], tools: [], certifications: [] },
      summary: 'Analysis unavailable',
      strengths: [],
      languageProficiency: { english: 'unknown', other: [] },
      assessment: { score: 5, reasoning: 'Unable to parse response' },
      totalExperienceYears: '',
      rawText,
      error: errorMessage || 'Failed to analyze CV',
      provider: this.getProviderName(),
      model: this.getModelName()
    };
  }

  /**
   * Analyze Immigration Profile - Implementation
   */
  async analyzeImmigrationProfile(userProfile, extractedData, cvText, options = {}) {
    try {
      console.log('[OpenAI Provider] Starting immigration analysis...');
      this.validateConfig();

      const prompt = IMMIGRATION_ANALYSIS_PROMPT(userProfile, extractedData, cvText);

      const completion = await this.client.chat.completions.create({
        model: this.model,
        messages: [
          {
            role: 'system',
            content: 'You are a senior immigration strategist. Provide comprehensive, data-driven immigration assessments based on CV analysis and user profiles. Return only valid JSON.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: options.temperature ?? 0.2,
        max_tokens: options.maxTokens || 4096,
        top_p: options.topP ?? 0.9,
        response_format: { type: 'json_object' }
      });

      const responseText = completion.choices[0]?.message?.content;

      if (!responseText) {
        throw new Error('No response from OpenAI');
      }

      const parsed = this.parseJSON(responseText);

      if (!parsed) {
        console.error('[OpenAI Provider] Failed to parse immigration response');
        const fallback = this.buildFallbackImmigrationAnalysis(userProfile, cvText);
        fallback.error = 'Failed to parse response';
        return fallback;
      }

      const fallback = this.buildFallbackImmigrationAnalysis(userProfile, cvText);
      const merged = this.deepMerge(fallback, parsed);

      return {
        ...merged,
        provider: this.getProviderName(),
        model: this.getModelName(),
        usage: {
          promptTokens: completion.usage?.prompt_tokens || 0,
          completionTokens: completion.usage?.completion_tokens || 0,
          totalTokens: completion.usage?.total_tokens || 0
        }
      };
    } catch (error) {
      console.error('[OpenAI Provider] Immigration analysis error:', error);
      const fallback = this.buildFallbackImmigrationAnalysis(userProfile, cvText);
      fallback.error = error.message;
      fallback.provider = this.getProviderName();
      fallback.model = this.getModelName();
      return fallback;
    }
  }

  /**
   * Deep merge helper
   */
  deepMerge(base = {}, override = {}) {
    const result = { ...base };
    Object.entries(override).forEach(([key, value]) => {
      if (value == null) return;

      if (Array.isArray(value)) {
        result[key] = value;
        return;
      }

      if (typeof value === 'object' && !Array.isArray(value)) {
        const baseChild = typeof base[key] === 'object' && base[key] !== null ? base[key] : {};
        result[key] = this.deepMerge(baseChild, value);
        return;
      }

      result[key] = value;
    });
    return result;
  }

  /**
   * Build fallback immigration analysis
   */
  buildFallbackImmigrationAnalysis(userProfile, cvText) {
    return {
      educationAssessment: {
        level: userProfile?.education?.highestEducation || "To be determined",
        institutions: [],
        fields: [],
        recognition: "Pending detailed evaluation",
        relevance: "Requires consultant review",
        institutionQuality: "Unknown",
        gpaStrength: "Not specified",
        additionalEducation: []
      },
      experienceAnalysis: {
        positions: [],
        totalYears: userProfile?.experience?.totalExperience || "0",
        relevantExperience: "0",
        careerProgression: "Standard profile",
        industryDemand: "Moderate",
        seniority: "Entry to Mid-level",
        companyReputation: "Standard",
        employmentGaps: "Not identified",
        currentSalary: "Not disclosed",
        leadershipExperience: "To be verified"
      },
      skillsEvaluation: {
        technicalSkills: {
          programming: [],
          frameworks: [],
          software: [],
          specializations: [],
          inDemandSkills: []
        },
        languageSkills: {
          english: userProfile?.languageSkills?.englishProficiency || "Unknown",
          german: "Not specified",
          otherLanguages: [],
          languageAdvantage: "Language test recommended"
        },
        softSkills: [],
        certifications: [],
        professionalMemberships: []
      },
      projectsAndAchievements: {
        notableProjects: [],
        awards: [],
        publications: [],
        speakingEngagements: []
      },
      immigrationStrengths: ["Profile under review"],
      potentialConcerns: ["Complete documentation review required"],
      recommendedPathways: [],
      selectedPathwayAnalysis: {
        pathway: userProfile?.reviewData?.selectedPathway || "To be determined",
        country: userProfile?.countrySelection?.selectedCountry || "Germany",
        eligibilityScore: 50,
        scoreBreakdown: {
          education: 10,
          experience: 10,
          language: 10,
          financialReadiness: 10,
          ageOptimality: 10
        },
        eligible: "Fair",
        strengths: ["Standard profile"],
        weaknesses: ["Requires detailed assessment"],
        missingRequirements: ["Complete documentation"],
        timelineEstimate: "6-12 months",
        successProbability: 50,
        improvementActions: ["Submit complete documentation"]
      },
      alternativePathways: [],
      salaryExpectations: {
        currentSalary: "Not specified",
        targetCountrySalary: "Market dependent",
        competitiveness: "Standard"
      },
      missingCriticalInfo: ["Detailed work experience", "Language test scores"],
      recommendedNextSteps: ["Schedule consultation"],
      documentChecklist: ["Passport", "Academic certificates"],
      visaReadiness: {
        status: "Pending assessment",
        reasoning: "Awaiting documentation",
        priorityActions: ["Collect certificates"]
      },
      overallAssessment: "Profile requires consultant review"
    };
  }

  /**
   * Health check with actual API call
   */
  async healthCheck() {
    try {
      this.validateConfig();

      // Simple test request
      await this.client.chat.completions.create({
        model: this.model,
        messages: [{ role: 'user', content: 'test' }],
        max_tokens: 5
      });

      return true;
    } catch (error) {
      console.error(`[OpenAI Provider] Health check failed:`, error);
      return false;
    }
  }
}

export default OpenAIProvider;
